import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import CButton from "../../components/CButton";
import CDataGrid from "../../components/CDataGrid";
import CTextField from "../../components/CTextField";
import moment from "moment";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import TrainingTypeCategoryMatrixDialog from "../../components/TrainingTypeCategoryMatrixDialog";
import CDialog from "../../components/CDialog";
import CFormProvider from "../../components/HookFormComponents/CFormProvider";
import { CFormSelect } from "../../components/HookFormComponents/CFormSelect";
import CFormTextField from "../../components/HookFormComponents/CFormTextField";
import CFormDatePicker from "../../components/HookFormComponents/CFormDatePicker";
import {
  updateTrainingAllocation,
  deleteTrainingAllocation,
} from "../../slices/bulkTrainingAllocationSlice";
import { fetchReviewData } from "../../slices/trainingFormReviewSlice";
import { fetchSetaTemplateByCompanyId } from "../../slices/setaTemplateSlice";
import { fetchYearId } from "../../slices/yearSlice";
import { getLocalData } from "../../constant/utils/commonUtils";
import { supabase } from "../../config/supabaseClient";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { ReactComponent as DeleteIcon } from "../../assets/images/trash-icon.svg";
import {
  TRAINING_CATEGORY_OPTIONS,
  trainingFormStatus,
  PROGRAM_DURATION_OPTIONS,
  EAP_REGIONS,
  statuses,
} from "../../constant/constants";
import { getStaticDataSourceSync } from "../../constant/staticDataSources";
import { toast } from "react-toastify";
import {
  Box,
  Popover,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@mui/material";

const BulkTrainingAllocation = () => {
  const dispatch = useDispatch();
  const { reviewFormData: reviewData, loading } = useSelector(
    (state) => state.trainingFormReview
  );

  const { formId: urlFormId } = useParams();
  const location = useLocation();
  const formIdFromState = location.state?.formId;
  const empIdFromState = location.state?.empId;

  // Use formId from URL params, location state, or undefined
  const formId = urlFormId || formIdFromState;
  const empId = empIdFromState;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);

  // State to track changes in each row's training category and type
  const [rowData, setRowData] = useState({});

  // Excel-style column filters
  const [columnFilters, setColumnFilters] = useState({});
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const [sortModel, setSortModel] = useState([]);
  const [availableTrainings, setAvailableTrainings] = useState([]);
  const [alertDialogState, setAlertDialogState] = useState({
    id: null,
    show: false,
  });

  // Store employee UUIDs mapped by form_id
  const [employeeUuidMap, setEmployeeUuidMap] = useState({});

  // State for Training Type Category Matrix dialog
  const [showCategoryMatrixDialog, setShowCategoryMatrixDialog] = useState(false);
  // State for Allocate Training Details modal (opens when user clicks Allocate Training)
  const [showAllocateModal, setShowAllocateModal] = useState(false);

  // SETA-derived training type options (fallback to PROGRAM_DURATION_OPTIONS)
  const setaFields = useSelector((state) => state.setaTemplate.setaTemplate);
  const setaName = useSelector((state) => state.setaTemplate.setaName);
  const [trainingTypeOptions, setTrainingTypeOptions] = useState(
    PROGRAM_DURATION_OPTIONS.map((opt) => ({
      id: opt.id,
      value: opt.value,
      label: opt.value,
    }))
  );

  // Load SETA template for this company (so we can extract training type allowed values)
  useEffect(() => {
    const companyId = getLocalData()?.company_id;
    if (companyId) {
      dispatch(fetchSetaTemplateByCompanyId(companyId));
    }
  }, [dispatch]);

  useEffect(() => {
    if (setaFields && Array.isArray(setaFields)) {
      const trainingTypeField = setaFields.find(
        (field) => field.config?.isTrainingTypeField
      );
      if (trainingTypeField && Array.isArray(trainingTypeField.allowedValues)) {
        const options = trainingTypeField.allowedValues.map((val) => ({
          id: val,
          value: val,
          label: val,
        }));
        setTrainingTypeOptions(options);
      }
    }
  }, [setaFields]);

  // Fetch all available active trainings
  useEffect(() => {
    const fetchActiveTrainings = async () => {
      try {
        const companyId = getLocalData()?.company_id;
        const { data, error } = await supabase
          .from("training_catalogue_data")
          .select("id, name, company_id, training_catalogue(name)")
          .eq("status", statuses.ACTIVE)
          .or(`company_id.is.null,company_id.eq.${companyId}`)
          .order("name", { ascending: true });

        if (error) throw error;
        setAvailableTrainings(
          (data || []).map((t) => {
            const catalogueName = t.training_catalogue?.name;
            const trainingName = t.name || catalogueName || "Unnamed Training";
            return {
              id: t.id,
              value: catalogueName && t.name ? `${t.name} (${catalogueName})` : trainingName,
              label: catalogueName && t.name ? `${t.name} (${catalogueName})` : trainingName,
            };
          })
        );
      } catch (error) {
        console.error("Error fetching active trainings:", error);
      }
    };
    fetchActiveTrainings();
  }, []);

  // Create a mapping of training ID to Name for quick lookups
  const trainingNameMap = useMemo(() => {
    const map = {};
    availableTrainings.forEach((t) => {
      map[t.id] = t.label;
    });
    return map;
  }, [availableTrainings]);

  const additionalFields = useMemo(
    () => setaFields?.filter((f) => f.config?.isAdditionalField) ?? [],
    [setaFields]
  );

  /** SETA field display names (replace hardcoded "Training Type" / "Training Category" labels) */
  const setaTrainingTypeField = useMemo(
    () =>
      Array.isArray(setaFields)
        ? setaFields.find((f) => f.config?.isTrainingTypeField)
        : null,
    [setaFields]
  );
  const setaTrainingCategoryField = useMemo(
    () =>
      Array.isArray(setaFields)
        ? setaFields.find((f) => f.config?.isTrainingCategoryField)
        : null,
    [setaFields]
  );
  const trainingTypeLabel =
    (setaTrainingTypeField?.fieldName || "").trim() || "Training Type";
  const trainingCategoryLabel =
    (setaTrainingCategoryField?.fieldName || "").trim() || "Training Category";
  const getAdditionalFieldOptions = (af) => {
    let values = Array.isArray(af.allowedValues) ? af.allowedValues : [];
    if (af.staticDataSourceName && values.length === 0) {
      const staticValues = getStaticDataSourceSync(af.staticDataSourceName);
      values = staticValues?.length > 0 ? staticValues : [];
      if (
        values.length === 0 &&
        String(af.staticDataSourceName).toLowerCase().trim() === "province"
      ) {
        values = EAP_REGIONS.filter((r) => !r.isNational).map((r) => r.name);
      }
    }
    return values;
  };

  const getAdditionalFieldKey = (field) =>
    `additional_${(field.fieldName || "").replace(/\s+/g, "_")}`;

  const buildAllocateDefaultValues = useCallback(() => {
    const d = {
      trainingCategory: "",
      trainingType: "",
      updateTraining: "",
    };
    additionalFields.forEach((af) => {
      d[getAdditionalFieldKey(af)] = "";
    });
    return d;
  }, [additionalFields]);

  const allocateFormMethods = useForm({
    defaultValues: { trainingCategory: "", trainingType: "", updateTraining: "" },
  });

  /** Normalize date values from CFormDatePicker (dayjs/Date/string) for API payload */
  const serializeAdditionalFieldValue = (af, raw) => {
    if (raw == null || raw === "") return "";
    const ft = (af.type || "string").toLowerCase();
    if (ft === "date") {
      if (typeof raw === "string") return raw;
      if (raw && typeof raw.format === "function") return raw.format("YYYY-MM-DD");
      if (raw instanceof Date) return moment(raw).format("YYYY-MM-DD");
      const d = dayjs(raw);
      return d.isValid() ? d.format("YYYY-MM-DD") : "";
    }
    if (ft === "number") {
      if (raw === "" || raw == null) return "";
      const n = Number(raw);
      return Number.isNaN(n) ? raw : n;
    }
    return raw;
  };

  const buildAdditionalFieldPayload = (row, rowDataRow) => {
    const obj = {};
    additionalFields.forEach((af) => {
      const key = getAdditionalFieldKey(af);
      const val = rowDataRow?.[key] ?? row[key];
      if (val !== "Not assigned" && val != null && val !== "")
        obj[af.fieldName] = val;
    });
    return obj;
  };

  // Add "Not assigned" option to both dropdowns
  const trainingCategoryOptionsWithNotAssigned = [
    { id: "Not assigned", value: "Not assigned", label: "Not assigned" },
    ...TRAINING_CATEGORY_OPTIONS,
  ];

  const trainingTypeOptionsWithNotAssigned = [
    { id: "Not assigned", value: "Not assigned", label: "Not assigned" },
    ...trainingTypeOptions,
  ];

  // Build employee UUID map from reviewData (no API call needed)
  // The get_users_with_training_and_manager function already returns "Employee UUID"
  useEffect(() => {
    if (!reviewData || reviewData.length === 0) {
      setEmployeeUuidMap({});
      return;
    }

    // Create a map of form_id -> Employee UUID directly from reviewData
    const uuidMap = {};
    reviewData.forEach((employee) => {
      if (employee.id && employee["Employee UUID"]) {
        uuidMap[employee.id] = employee["Employee UUID"];
      }
    });

    setEmployeeUuidMap(uuidMap);
  }, [reviewData]);

  // Transform review data to flattened format for Bulk Training Allocation
  const transformedData = useMemo(() => {
    if (!reviewData || reviewData.length === 0) return [];

    const flattened = [];
    reviewData.forEach((employee) => {
      const selectedTrainings = employee.selectedTrainings || [];
      // Get employee UUID from the map (built from "Employee UUID" field in reviewData)
      // employee.id is the form_id (training_survey_form.id)
      // "Employee UUID" is already returned by get_users_with_training_and_manager function
      const employeeUuid =
        employeeUuidMap[employee.id] ||
        employee["Employee UUID"] ||
        employee.emp_id ||
        employee.employee_id ||
        employee.company_user_id ||
        null;

      // If employee has selected trainings, create one row per training
      if (selectedTrainings.length > 0) {
        selectedTrainings.forEach((training, index) => {
          const row = {
            id: `${employee.id}-${training.id || index}`,
            form_id: employee.id,
            employeeName: employee["Employee Name"] || "-",
            department: employee.department || "-",
            jobTitle: employee.job || "-",
            reportingManager: employee["Manager Name"] || "-",
            trainingName:
              training.name ||
              trainingNameMap[training.id] ||
              trainingNameMap[training.selected_training_id] ||
              "-",
            trainingCategory: training.training_category || "Not assigned",
            trainingType: training.training_type || "Not assigned",
            selected_training_id: training.id || null,
            empId: employeeUuid,
          };
          additionalFields.forEach((af) => {
            const key = getAdditionalFieldKey(af);
            const raw =
              training.allocated_additional_field?.[af.fieldName] ?? null;
            row[key] =
              raw !== null && raw !== undefined && raw !== ""
                ? String(raw)
                : "Not assigned";
          });
          flattened.push(row);
        });
      } else {
        // Only show employees with no selected trainings if they are NOT in Recommended status.
        // For Recommended status, we only show those who have at least one training assigned.
        const empStatus = employee.Status || employee.status || "";
        const isRecommended = empStatus === trainingFormStatus.RECOMMENDED;

        if (!isRecommended) {
          flattened.push({
            id: `${employee.id}-no-training`,
            form_id: employee.id,
            employeeName: employee["Employee Name"] || "-",
            department: employee.department || "-",
            jobTitle: employee.job || "-",
            reportingManager: employee["Manager Name"] || "-",
            trainingName: "-",
            trainingCategory: "Not assigned",
            trainingType: "Not assigned",
            selected_training_id: null,
            empId: employeeUuid,
          });
        }
      }
    });

    return flattened;
  }, [reviewData, employeeUuidMap, additionalFields]);

  // Get unique values for a column (for Excel-style filter)
  const getCellValue = (item, field) => {
    if (field === "trainingCategory")
      return rowData[item.id]?.trainingCategory ?? item.trainingCategory;
    if (field === "trainingType")
      return rowData[item.id]?.trainingType ?? item.trainingType;
    if (additionalFields.some((af) => getAdditionalFieldKey(af) === field))
      return rowData[item.id]?.[field] ?? item[field];
    return item[field];
  };

  const getUniqueColumnValues = (field) => {
    if (!transformedData || transformedData.length === 0) return [];
    const values = new Set();
    transformedData.forEach((item) => {
      const value = getCellValue(item, field);
      if (value !== null && value !== undefined && value !== "") {
        values.add(value);
      }
    });
    return Array.from(values).sort();
  };

  // Filter and search data
  const filteredData = useMemo(() => {
    if (!transformedData || transformedData.length === 0) return [];

    let filtered = [...transformedData];

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.employeeName?.toLowerCase().includes(query) ||
          item.department?.toLowerCase().includes(query) ||
          item.jobTitle?.toLowerCase().includes(query) ||
          item.reportingManager?.toLowerCase().includes(query) ||
          item.trainingName?.toLowerCase().includes(query)
      );
    }

    // Apply Excel-style column filters
    Object.keys(columnFilters).forEach((field) => {
      const filterValues = columnFilters[field];
      if (filterValues && filterValues.length > 0) {
        filtered = filtered.filter((item) =>
          filterValues.includes(getCellValue(item, field))
        );
      }
    });

    // Apply sorting
    if (sortModel.length > 0) {
      const sort = sortModel[0];
      filtered.sort((a, b) => {
        let aValue = getCellValue(a, sort.field);
        let bValue = getCellValue(b, sort.field);
        if (aValue === null || aValue === undefined) aValue = "";
        if (bValue === null || bValue === undefined) bValue = "";
        const comparison = String(aValue).localeCompare(String(bValue));
        return sort.sort === "asc" ? comparison : -comparison;
      });
    }

    return filtered;
  }, [
    transformedData,
    searchQuery,
    rowData,
    columnFilters,
    sortModel,
    additionalFields,
  ]);

  // Fetch data on component mount or when formId/empId changes
  useEffect(() => {
    // Use fetchReviewData with viewAll=true and statusFilter="Approved" to get only recommended training forms
    dispatch(fetchReviewData({ departmentId: null, viewAll: true, statusFilter: trainingFormStatus.RECOMMENDED }));
  }, [dispatch, formId, empId]);

  // Initialize rowData when transformedData changes
  useEffect(() => {
    if (transformedData && transformedData.length > 0) {
      const initialRowData = {};
      transformedData.forEach((item) => {
        const base = {
          trainingCategory: item.trainingCategory || "Not assigned",
          trainingType: item.trainingType || "Not assigned",
        };
        additionalFields.forEach((af) => {
          const key = getAdditionalFieldKey(af);
          base[key] = item[key] ?? "Not assigned";
        });
        initialRowData[item.id] = base;
      });
      setRowData(initialRowData);
    }
  }, [transformedData, additionalFields]);

  // Handle row selection
  const handleRowSelection = (newSelection) => {
    setSelectedRows(newSelection);
  };

  // Handle training category change for a specific row
  const handleTrainingCategoryChange = (rowId, value) => {
    setRowData((prev) => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        trainingCategory: value,
      },
    }));
  };

  // Handle training type change for a specific row
  const handleTrainingTypeChange = (rowId, value) => {
    setRowData((prev) => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        trainingType: value,
      },
    }));
  };

  // Handle select all rows
  const handleSelectAll = () => {
    if (selectedRows.length === filteredData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredData.map((row) => row.id));
    }
  };

  // Handle delete training allocation
  const handleDeleteAllocation = async (selectedTrainingId) => {
    if (!selectedTrainingId) {
      toast.error("Invalid selection for deletion");
      return;
    }
    try {
      await dispatch(deleteTrainingAllocation({ selectedTrainingId })).unwrap();
      toast.success("Training allocation removed successfully");
      // Refresh data
      await dispatch(fetchReviewData({ departmentId: null, viewAll: true, statusFilter: trainingFormStatus.RECOMMENDED })).unwrap();
    } catch (error) {
      console.error("Error removing training allocation:", error);
      toast.error("Error removing training allocation");
    } finally {
      setAlertDialogState({ id: null, show: false });
    }
  };

  // Handle save allocation for selected rows or all rows
  const handleSaveAllocation = async () => {
    try {
      const rowsToSave =
        selectedRows.length > 0
          ? filteredData.filter((item) => selectedRows.includes(item.id))
          : filteredData;

      if (rowsToSave.length === 0) {
        toast.error("No rows to save");
        return;
      }

      // Get yearId and companyId
      const companyId = getLocalData()?.company_id;
      if (!companyId) {
        toast.error("Company ID not found");
        return;
      }

      const yearRes = await dispatch(fetchYearId());
      const yearId = yearRes.payload;
      if (!yearId) {
        toast.error("Year ID not found");
        return;
      }

      // Prepare updates - use employeeUuidMap to get empId (no API calls needed)
      const updatesPromises = rowsToSave.map(async (row) => {
        const rowUpdates = rowData[row.id];
        // Get empId from the map we built from reviewData, or use row.empId as fallback
        let empId = employeeUuidMap[row.form_id] || row.empId || null;

        // Check if empId is a valid UUID format (contains hyphens and is 36 chars)
        // If not valid or missing, fetch it from the form
        const isUUID =
          empId &&
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            empId
          );

        if ((!isUUID || !empId) && row.form_id) {
          try {
            const { data: formData, error: formError } = await supabase
              .from("training_survey_form")
              .select("emp_id")
              .eq("id", row.form_id)
              .single();

            if (!formError && formData?.emp_id) {
              empId = formData.emp_id;
            }
          } catch (error) {
            // Error fetching employee UUID from form
          }
        }

        return {
          selectedTrainingId: row.selected_training_id,
          trainingCategory:
            rowUpdates?.trainingCategory || row.trainingCategory,
          trainingType: rowUpdates?.trainingType || row.trainingType,
          formId: row.form_id,
          empId: empId,
          companyId: companyId,
          yearId: yearId,
          additionalField: buildAdditionalFieldPayload(row, rowUpdates),
        };
      });

      const updates = await Promise.all(updatesPromises);
      await dispatch(
        updateTrainingAllocation({
          updates,
          comment: "Training allocation updated via Bulk Training Allocation",
        })
      ).unwrap();

      toast.success(`Allocation saved for ${rowsToSave.length} training(s)`);

      // Refresh data - only load recommended (Approved) status forms
      dispatch(fetchReviewData({ departmentId: null, viewAll: true, statusFilter: trainingFormStatus.RECOMMENDED }));

      // Clear selection after save
      setSelectedRows([]);
    } catch (error) {
      toast.error("Error saving allocation");
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery("");
    setColumnFilters({});
    setSortModel([]);
  };

  // Handle bulk assignment of category and type (returns true on success, false on failure)
  const handleBulkAssign = async () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one row");
      return false;
    }

    const values = allocateFormMethods.getValues();
    const selectedCategoryForAssign = values.trainingCategory || "";
    const selectedTypeForAssign = values.trainingType || "";
    const selectedTrainingIdForUpdate = values.updateTraining || "";
    const selectedAdditionalForAssign = {};
    additionalFields.forEach((af) => {
      const key = getAdditionalFieldKey(af);
      selectedAdditionalForAssign[key] = serializeAdditionalFieldValue(
        af,
        values[key]
      );
    });

    const hasAdditionalSelection = Object.values(
      selectedAdditionalForAssign
    ).some((v) => v != null && v !== "");
    if (
      !selectedCategoryForAssign &&
      !selectedTypeForAssign &&
      !selectedTrainingIdForUpdate &&
      !hasAdditionalSelection
    ) {
      toast.warning(
        "Please select at least one category, type, training, or additional field to assign"
      );
      return false;
    }

    try {
      const updatedRowData = { ...rowData };
      selectedRows.forEach((rowId) => {
        updatedRowData[rowId] = {
          ...updatedRowData[rowId],
          ...(selectedCategoryForAssign && {
            trainingCategory: selectedCategoryForAssign,
          }),
          ...(selectedTypeForAssign && { trainingType: selectedTypeForAssign }),
          ...Object.fromEntries(
            Object.entries(selectedAdditionalForAssign)
              .filter(([, v]) => v != null && v !== "")
              .map(([k, v]) => [k, v])
          ),
        };
      });
      setRowData(updatedRowData);

      // Get the rows to save
      const rowsToSave = filteredData.filter((item) =>
        selectedRows.includes(item.id)
      );
      if (rowsToSave.length === 0) {
        toast.error("No rows to save");
        return;
      }

      // Get yearId and companyId
      const companyId = getLocalData()?.company_id;
      if (!companyId) {
        toast.error("Company ID not found");
        return;
      }

      const yearRes = await dispatch(fetchYearId());
      const yearId = yearRes.payload;
      if (!yearId) {
        toast.error("Year ID not found");
        return;
      }

      // Prepare updates for saving - use employeeUuidMap to get empId (no API calls needed)
      const updatesPromises = rowsToSave.map(async (row) => {
        const rowUpdates = updatedRowData[row.id];
        // Get empId from the map we built from reviewData, or use row.empId as fallback
        let empId = employeeUuidMap[row.form_id] || row.empId || null;

        // Check if empId is a valid UUID format (contains hyphens and is 36 chars)
        // If not valid or missing, fetch it from the form
        const isUUID =
          empId &&
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            empId
          );

        if (!isUUID && row.form_id) {
          try {
            const { data: formData, error: formError } = await supabase
              .from("training_survey_form")
              .select("emp_id")
              .eq("id", row.form_id)
              .single();

            if (!formError && formData?.emp_id) {
              empId = formData.emp_id;
            }
          } catch (error) {
            // Error fetching employee UUID from form
          }
        }

        return {
          selectedTrainingId: row.selected_training_id,
          trainingCategory:
            rowUpdates?.trainingCategory || row.trainingCategory,
          trainingType: rowUpdates?.trainingType || row.trainingType,
          formId: row.form_id,
          empId: empId,
          companyId: companyId,
          yearId: yearId,
          additionalField: buildAdditionalFieldPayload(row, rowUpdates),
        };
      });

      const updates = await Promise.all(updatesPromises);
      const trainingIdUpdates = selectedTrainingIdForUpdate
        ? rowsToSave
            .filter((row) => row.selected_training_id)
            .map((row) => ({
              selectedTrainingId: row.selected_training_id,
              newTrainingId: selectedTrainingIdForUpdate,
            }))
        : [];

      await dispatch(
        updateTrainingAllocation({
          updates,
          trainingIdUpdates,
          comment: "Training allocation updated via Bulk Training Allocation",
        })
      ).unwrap();

      const assignedItems = [];
      if (selectedCategoryForAssign)
        assignedItems.push(`Category: "${selectedCategoryForAssign}"`);
      if (selectedTypeForAssign)
        assignedItems.push(`Type: "${selectedTypeForAssign}"`);
      if (selectedTrainingIdForUpdate) {
        const trainingName = availableTrainings.find(
          (t) => t.id === selectedTrainingIdForUpdate
        )?.label;
        assignedItems.push(`Training: "${trainingName}"`);
      }
      Object.entries(selectedAdditionalForAssign).forEach(([key, val]) => {
        if (val != null && val !== "") {
          const label =
            additionalFields.find((af) => getAdditionalFieldKey(af) === key)
              ?.fieldName || key;
          assignedItems.push(`${label}: "${val}"`);
        }
      });

      toast.success(
        `${assignedItems.join(", ")} assigned and saved for ${selectedRows.length
        } training(s)`
      );

      allocateFormMethods.reset(buildAllocateDefaultValues());

      // Refresh data - only load recommended (Approved) status forms
      dispatch(fetchReviewData({ departmentId: null, viewAll: true, statusFilter: trainingFormStatus.RECOMMENDED }));

      // Clear selection after save
      setSelectedRows([]);
      return true;
    } catch (error) {
      toast.error("Error saving allocation");
      return false;
    }
  };

  // Excel-style column filter handlers
  const handleFilterClick = (event, field) => {
    setFilterAnchorEl(event.currentTarget);
    setActiveFilterColumn(field);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setActiveFilterColumn(null);
  };

  const handleColumnFilterChange = (field, value) => {
    setColumnFilters((prev) => {
      const currentFilters = prev[field] || [];
      if (currentFilters.includes(value)) {
        const newFilters = currentFilters.filter((f) => f !== value);
        if (newFilters.length === 0) {
          const { [field]: removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [field]: newFilters };
      } else {
        return { ...prev, [field]: [...currentFilters, value] };
      }
    });
  };

  const handleSelectAllColumnFilter = (field) => {
    const uniqueValues = getUniqueColumnValues(field);
    const currentFilters = columnFilters[field] || [];
    if (currentFilters.length === uniqueValues.length) {
      // Deselect all
      const { [field]: removed, ...rest } = columnFilters;
      setColumnFilters(rest);
    } else {
      // Select all
      setColumnFilters((prev) => ({ ...prev, [field]: uniqueValues }));
    }
  };

  const handleClearColumnFilter = (field) => {
    const { [field]: removed, ...rest } = columnFilters;
    setColumnFilters(rest);
  };

  const handleSort = (field) => {
    setSortModel((prev) => {
      const existingSort = prev.find((s) => s.field === field);
      if (!existingSort) {
        return [{ field, sort: "asc" }];
      } else if (existingSort.sort === "asc") {
        return [{ field, sort: "desc" }];
      } else {
        return [];
      }
    });
  };

  // Custom header renderer with filter icon
  const renderHeaderWithFilter = (field, headerName, showInfoIcon = false) => {
    const currentFilters = columnFilters[field] || [];
    const hasFilter = currentFilters.length > 0;
    const currentSort = sortModel.find((s) => s.field === field);

    return (
      <div className="flex items-center justify-between w-full pr-2">
        <span className="font-semibold">{headerName}</span>
        <div className="flex items-center">
          {/* Info icon for Training Category column */}
          {showInfoIcon && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setShowCategoryMatrixDialog(true);
              }}
              className="!p-1 !ml-1"
              sx={{
                color: "#1976d2",
                "&:hover": {
                  color: "#1565c0",
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                },
              }}
              title="View Training Type Category Matrix"
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
          {/* Filter icon */}
          <IconButton
            size="small"
            onClick={(e) => handleFilterClick(e, field)}
            className="!p-1 !ml-2"
            sx={{
              color: hasFilter ? "#1976d2" : "#9e9e9e",
              "&:hover": {
                color: "#1976d2",
                backgroundColor: "rgba(25, 118, 210, 0.08)",
              },
            }}
          >
            <FilterListIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </div>
      </div>
    );
  };

  const columns = [
    {
      field: "employeeName",
      headerName: "Employee Name",
      minWidth: 150,
      renderHeader: () =>
        renderHeaderWithFilter("employeeName", "Employee Name"),
    },
    {
      field: "department",
      headerName: "Department",
      minWidth: 150,
      renderHeader: () => renderHeaderWithFilter("department", "Department"),
    },
    {
      field: "jobTitle",
      headerName: "Job Title",
      minWidth: 150,
      renderHeader: () => renderHeaderWithFilter("jobTitle", "Job Title"),
    },
    {
      field: "reportingManager",
      headerName: "Reporting Manager",
      minWidth: 180,
      renderHeader: () =>
        renderHeaderWithFilter("reportingManager", "Reporting Manager"),
    },
    {
      field: "trainingName",
      headerName: "Training Name",
      flex: 1,
      minWidth: 200,
      renderHeader: () =>
        renderHeaderWithFilter("trainingName", "Training Name"),
    },
    {
      field: "trainingCategory",
      headerName: trainingCategoryLabel,
      minWidth: 180,
      renderHeader: () =>
        renderHeaderWithFilter("trainingCategory", trainingCategoryLabel, true),
      renderCell: (params) => {
        const currentValue =
          rowData[params.row.id]?.trainingCategory ||
          params.row.trainingCategory ||
          "Not assigned";
        return (
          <span className="text-sm">
            {currentValue === "Not assigned" || !currentValue ? "-" : currentValue}
          </span>
        );
      },
    },
    {
      field: "trainingType",
      headerName: trainingTypeLabel,
      minWidth: 150,
      renderHeader: () =>
        renderHeaderWithFilter("trainingType", trainingTypeLabel),
      renderCell: (params) => {
        const currentValue =
          rowData[params.row.id]?.trainingType ||
          params.row.trainingType ||
          "Not assigned";
        return (
          <span className="text-sm">
            {currentValue === "Not assigned" || !currentValue ? "-" : currentValue}
          </span>
        );
      },
    },
    ...additionalFields.map((af) => {
      const fieldKey = getAdditionalFieldKey(af);
      return {
        field: fieldKey,
        headerName: af.fieldName,
        minWidth: 150,
        renderHeader: () =>
          renderHeaderWithFilter(fieldKey, af.fieldName),
        renderCell: (params) => {
          const currentValue =
            rowData[params.row.id]?.[fieldKey] ??
            params.row[fieldKey] ??
            "Not assigned";
          return (
            <span className="text-sm">
              {currentValue === "Not assigned" || !currentValue
                ? "-"
                : currentValue}
            </span>
          );
        },
      };
    }),
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <div className="flex items-center h-full">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setAlertDialogState({
                id: params.row.selected_training_id,
                show: true,
              });
            }}
            className="!p-1 hover:bg-red-50 group"
            title="Delete Training Allocation"
          >
            <DeleteIcon
              className="!fill-red-500 group-hover:!fill-red-700"
              style={{ width: 20, height: 20 }}
            />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <div>
      <h2 className="2xl:text-3xl 2xl:leading-3xl text-2xl leading-2xl text-blue-500 font-semibold 2xl:mb-4 md:mb-3 mb-4">
        Bulk Training Allocation
      </h2>

      {/* Instructions Section */}
      <div className="border-2 border-blue-500 bg-white rounded-lg p-4 md:mb-6 mb-4 relative">
        <p className="bg-white text-lg font-semibold text-gray-700 mb-4 2xl:text-xl 2xl:leading-xl lg:text-base lg:leading-base text-sm leading-sm absolute md:top-[-16px] top-[-12px] left-[16px] px-1">
          Instructions
        </p>
        <div>
          <p className="font-medium mb-0 2xl:text-base text-sm 2xl:leading-base leading-sm text-gray-300">
            Use this page to assign Training Category, Training Type, and any additional allocation fields to multiple trainings at once. Select one or more rows from the table, then click &quot;Allocate Training&quot; to open the allocation form. Choose values for Training Category, Training Type, and SETA fields in the form, then click &quot;Add&quot; to apply the selected values to all selected rows and save.
          </p>
        </div>
      </div>

      {/* Combined Search, Filters and Data Table Section */}
      <div className="bg-white border border-solid border-blue-200 rounded-xl overflow-hidden">
        {/* Search and Allocate Training button */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 p-3">
          <CTextField
            name="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            startIcon={<SearchIcon className="text-blue-300" />}
            placeholder="Search"
            className="!mt-0 w-full sm:w-[180px] md:w-[200px] shrink-0"
          />
          <CButton
            onClick={() => {
              if (selectedRows.length === 0) {
                toast.warning("Please select at least one row");
                return;
              }
              allocateFormMethods.reset(buildAllocateDefaultValues());
              setShowAllocateModal(true);
            }}
            className="bg-blue-500 hover:bg-blue-700 text-white shrink-0"
            disabled={loading || selectedRows.length === 0}
          >
            Allocate Training
          </CButton>
        </div>

        {/* Data Table — taller instructions than employee list; subtract ~40px more from vh */}
        <div className="2xl:h-[calc(100vh-389px)] md:h-[calc(100vh-361px)] h-[calc(100vh-431px)] w-full overflow-hidden">
          <CDataGrid
            idKey={"id"}
            searchQuery={""}
            rows={filteredData}
            columns={columns}
            checkboxSelection
            onRowSelectionModelChange={handleRowSelection}
            rowSelectionModel={selectedRows}
            loading={loading}
            initialPageSize={10}
          />
        </div>
      </div>

      {/* Allocate Training Details — FormProvider + generic CForm* components */}
      <CDialog
        open={showAllocateModal}
        onClose={() => {
          setShowAllocateModal(false);
          allocateFormMethods.reset(buildAllocateDefaultValues());
        }}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 0,
            width: "calc(100% - 32px)",
            maxWidth: 560,
            minHeight: 400,
            m: 2,
            maxHeight: "calc(100vh - 32px)",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 10px 40px rgba(15, 23, 42, 0.12)",
          },
        }}
      >
        <div className="flex flex-col flex-1 min-h-0 p-5 sm:p-6 !font-[Poppins] allocate-training-modal text-sm text-gray-800">
          <div className="flex items-center justify-between gap-3 mb-5 flex-shrink-0">
            <h3 className="text-base sm:text-lg font-semibold text-blue-500 leading-tight pr-2">
              Allocate Training Details
            </h3>
            <IconButton
              onClick={() => {
                setShowAllocateModal(false);
                allocateFormMethods.reset(buildAllocateDefaultValues());
              }}
              size="small"
              sx={{ color: "#6b7280", padding: "4px", flexShrink: 0 }}
              aria-label="Close"
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </div>

          <CFormProvider
            methods={allocateFormMethods}
            onSubmit={allocateFormMethods.handleSubmit(async () => {
              const success = await handleBulkAssign();
              if (success) setShowAllocateModal(false);
            })}
            className="custom-input-group flex flex-col flex-1 min-h-0"
          >
            <div className="allocate-form-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-1 space-y-4">
              <div className="allocate-modal-field-row grid grid-cols-[minmax(120px,148px)_minmax(0,1fr)] gap-x-2 items-center min-h-[36px]">
                <label className="text-sm font-medium text-gray-700 leading-snug pr-1">
                  {trainingCategoryLabel}
                </label>
                <div className="min-w-0 allocate-modal-input-wrap [&_.custom-select]:!h-9 [&_.custom-select]:!min-h-[36px] [&_.custom-select]:!py-0">
                  <CFormSelect
                    name="trainingCategory"
                    placeholder={`Select ${trainingCategoryLabel}`}
                    options={trainingCategoryOptionsWithNotAssigned}
                  />
                </div>
              </div>
              <div className="allocate-modal-field-row grid grid-cols-[minmax(120px,148px)_minmax(0,1fr)] gap-x-2 items-center min-h-[36px]">
                <label className="text-sm font-medium text-gray-700 leading-snug pr-1">
                  {trainingTypeLabel}
                </label>
                <div className="min-w-0 allocate-modal-input-wrap [&_.custom-select]:!h-9 [&_.custom-select]:!min-h-[36px] [&_.custom-select]:!py-0">
                  <CFormSelect
                    name="trainingType"
                    placeholder={`Select ${trainingTypeLabel}`}
                    options={trainingTypeOptionsWithNotAssigned}
                  />
                </div>
              </div>
              <div className="allocate-modal-field-row grid grid-cols-[minmax(120px,148px)_minmax(0,1fr)] gap-x-2 items-center min-h-[36px]">
                <label className="text-sm font-medium text-gray-700 leading-snug pr-1">
                  {"Training Name"}
                </label>
                <div className="min-w-0 allocate-modal-input-wrap [&_.custom-select]:!h-9 [&_.custom-select]:!min-h-[36px] [&_.custom-select]:!py-0">
                  <CFormSelect
                    name="updateTraining"
                    placeholder={`Select Training Name`}
                    options={availableTrainings}
                  />
                </div>
              </div>
              {additionalFields.map((af) => {
                const fieldKey = getAdditionalFieldKey(af);
                const fieldType = (af.type || "string").toLowerCase();
                const optionValues = getAdditionalFieldOptions(af);
                const hasOptions =
                  fieldType === "dropdown" || optionValues.length > 0;
                const inputWrapperClass =
                  "min-w-0 allocate-modal-input-wrap [&_.custom-select]:!h-9 [&_.custom-select]:!min-h-[36px] [&_.custom-select]:!py-0";
                return (
                  <div
                    key={fieldKey}
                    className="allocate-modal-field-row grid grid-cols-[minmax(120px,148px)_minmax(0,1fr)] gap-x-2 items-center min-h-[36px]"
                  >
                    <label className="text-sm font-medium text-gray-700 leading-snug pr-1">
                      {af.fieldName}
                    </label>
                    <div
                      className={
                        hasOptions
                          ? inputWrapperClass
                          : fieldType === "date"
                            ? "min-w-0 allocate-modal-date-wrap"
                            : "min-w-0"
                      }
                    >
                      {hasOptions ? (
                        <CFormSelect
                          name={fieldKey}
                          placeholder={`Select ${af.fieldName}`}
                          options={optionValues.map((v) => ({
                            id: v,
                            value: v,
                            label: v,
                          }))}
                        />
                      ) : fieldType === "date" ? (
                        <Box
                          className="allocate-modal-date-wrap w-full"
                          sx={{
                            width: "100%",
                            "& .MuiInputBase-root": {
                              height: 36,
                              minHeight: 36,
                              fontSize: "0.875rem",
                              backgroundColor: "#fff",
                            },
                            "& .MuiInputBase-input": {
                              py: 1,
                              fontSize: "0.875rem",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#e5e7eb",
                            },
                            "& .MuiIconButton-root": {
                              p: 0.5,
                              "& svg": { width: 18, height: 18 },
                            },
                          }}
                        >
                          <CFormDatePicker
                            name={fieldKey}
                            label=""
                            className="allocate-modal-datepicker !mt-0 w-full"
                          />
                        </Box>
                      ) : fieldType === "number" ? (
                        <CFormTextField
                          name={fieldKey}
                          type="number"
                          placeholder={af.fieldName}
                          className="table-inputs 2xl:!mt-0 !mt-0 w-full allocate-modal-ctextfield"
                        />
                      ) : (
                        <CFormTextField
                          name={fieldKey}
                          placeholder={af.fieldName}
                          className="table-inputs 2xl:!mt-0 !mt-0 w-full allocate-modal-ctextfield"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between items-center gap-3 mt-6 pt-4 border-t border-gray-200 flex-shrink-0">
              <CButton
                type="submit"
                className="bg-blue-500 hover:bg-blue-600 text-white !h-10 !text-sm !px-6 !py-2 !rounded-lg !font-medium !min-w-[100px]"
                disabled={loading}
              >
                Add
              </CButton>
              <CButton
                type="button"
                onClick={() => {
                  setShowAllocateModal(false);
                  allocateFormMethods.reset(buildAllocateDefaultValues());
                }}
                className="border border-blue-500 text-blue-500 bg-white hover:bg-blue-50 !h-10 !text-sm !px-6 !py-2 !rounded-lg !font-medium !min-w-[100px]"
              >
                Cancel
              </CButton>
            </div>
          </CFormProvider>
        </div>
      </CDialog>

      {/* Excel-style Filter Popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            minWidth: 250,
            maxWidth: 300,
            maxHeight: 400,
            mt: 1,
            p: 2,
          },
        }}
      >
        {activeFilterColumn && (
          <Box>
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold text-sm text-gray-700">
                Filter:{" "}
                {columns.find((c) => c.field === activeFilterColumn)
                  ?.headerName || activeFilterColumn}
              </span>
              <CButton
                onClick={() => handleClearColumnFilter(activeFilterColumn)}
                className="text-xs !h-8 !px-2 text-blue-500 hover:bg-blue-50"
              >
                Clear
              </CButton>
            </div>
            <div className="mb-2 pb-2 border-b">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      (columnFilters[activeFilterColumn] || []).length ===
                      getUniqueColumnValues(activeFilterColumn).length
                    }
                    indeterminate={
                      (columnFilters[activeFilterColumn] || []).length > 0 &&
                      (columnFilters[activeFilterColumn] || []).length <
                      getUniqueColumnValues(activeFilterColumn).length
                    }
                    onChange={() =>
                      handleSelectAllColumnFilter(activeFilterColumn)
                    }
                  />
                }
                label="Select All"
                className="text-sm"
              />
            </div>
            <div className="max-h-64 overflow-y-auto">
              {getUniqueColumnValues(activeFilterColumn).map((value) => (
                <FormControlLabel
                  key={value}
                  control={
                    <Checkbox
                      checked={(
                        columnFilters[activeFilterColumn] || []
                      ).includes(value)}
                      onChange={() =>
                        handleColumnFilterChange(activeFilterColumn, value)
                      }
                    />
                  }
                  label={<span className="text-sm">{value || "(Blank)"}</span>}
                  className="block mb-1"
                />
              ))}
            </div>
          </Box>
        )}
      </Popover>

      {/* Training Type Category Matrix Dialog */}
      <TrainingTypeCategoryMatrixDialog
        open={showCategoryMatrixDialog}
        onClose={() => setShowCategoryMatrixDialog(false)}
      />

      <ConfirmationDialog
        open={alertDialogState.show}
        onClose={() => setAlertDialogState({ id: null, show: false })}
        onConfirm={async () => {
          await handleDeleteAllocation(alertDialogState.id);
        }}
        title="Delete Training Allocation"
        description="Are you sure you want to delete this training allocation? This will remove the training from the employee's plan."
        confirmText="Delete"
        cancelText="Cancel"
        confirmClass="bg-blue-500 text-white hover:bg-blue-700 border border-blue-500 border-blue-700 px-4 py-2 rounded-lg"
        cancelClass="bg-white text-blue-500 hover:text-white hover:bg-blue-500 border border-blue-500 px-4 py-2 rounded-lg"
      />
    </div>
  );
};

export default BulkTrainingAllocation;
